"use client"

/**
 * /pricing — ported from the approved "Pricing.dc.html" design canvas
 * (desktop 1440 frame = source of truth). Dark-cyan v2 marketing system:
 * runs inside the shared <div className="vx-root"> shell that page.tsx wraps
 * around <VelovaNav /> + this body + <VelovaFooter />.
 *
 * Plan numbers are taken verbatim from the design. The monthly/annual toggle
 * and the billing-questions accordion are the only interactive pieces.
 */

import { useState } from "react"
import Link from "next/link"

type Billing = "monthly" | "annual"

const MONO = "var(--vx-mono)"
const SERIF = "var(--vx-serif)"

const CONTAINER: React.CSSProperties = {
  maxWidth: 1280,
  margin: "0 auto",
  paddingInline: "clamp(22px, 5vw, 80px)",
}

/* ─── Plan data (from the desktop 1440 frame) ───────────────────────────── */
interface PlanFeature {
  text: string
  color?: string
}
interface Plan {
  id: string
  name: string
  bestFor: string
  priceM: string | null
  priceA: string | null
  noteM: string
  featured: boolean
  border: string
  background: string
  boxShadow?: string
  nameColor: string
  bestForColor: string
  featureColor: string
  borderTop: string
  features: PlanFeature[]
  cta: string
  href: string
}

const AMBER = "rgba(228,203,162,.85)"
const CYAN_SOFT = "rgba(190,233,245,.9)"

const PLANS_DATA: Plan[] = [
  {
    id: "starter",
    name: "Starter",
    bestFor: "Founders and smaller teams",
    priceM: "$99",
    priceA: "$990",
    noteM: "$990/year billed annually",
    featured: false,
    border: "1px solid rgba(160,196,224,.14)",
    background: "linear-gradient(180deg, #05080D, #03050A)",
    nameColor: "rgba(232,238,244,.8)",
    bestForColor: "rgba(232,238,244,.5)",
    featureColor: "rgba(232,238,244,.75)",
    borderTop: "1px solid rgba(160,196,224,.12)",
    features: [
      { text: "1 brand · 50 prompts per engine" },
      { text: "ChatGPT · Gemini" },
      { text: "Daily runs, citations every run" },
      { text: "15 Actions/month · 3 approaches each" },
      { text: "30 content pieces · 1 AEO scan" },
      { text: "400 credits/month" },
      { text: "Claude not available on Starter", color: AMBER },
    ],
    cta: "Start free",
    href: "/signup",
  },
  {
    id: "pro",
    name: "Pro · Core",
    bestFor: "Growth marketing teams",
    priceM: "$349",
    priceA: "$3,490",
    noteM: "$3,490/year billed annually",
    featured: true,
    border: "1px solid rgba(107,213,236,.45)",
    background: "linear-gradient(180deg, rgba(107,213,236,.1), #04070C 60%)",
    boxShadow: "0 30px 70px -40px rgba(107,213,236,.5)",
    nameColor: "#DEF6FC",
    bestForColor: "rgba(207,238,247,.6)",
    featureColor: "rgba(240,245,249,.85)",
    borderTop: "1px solid rgba(107,213,236,.18)",
    features: [
      { text: "1 brand · 100 prompts per engine" },
      { text: "ChatGPT · Gemini · Google AI Mode · AI Overviews · Perplexity" },
      { text: "Daily runs, citations every run" },
      { text: "20 Actions/month · 3 approaches each" },
      { text: "60 content pieces · 2 AEO scans" },
      { text: "1,000 credits/month" },
      { text: "Claude add-on $79/month", color: CYAN_SOFT },
    ],
    cta: "Start free",
    href: "/signup",
  },
  {
    id: "scale",
    name: "Scale · Multi-brand",
    bestFor: "Multi-brand teams and agencies",
    priceM: "$899",
    priceA: "$8,990",
    noteM: "$8,990/year billed annually",
    featured: false,
    border: "1px solid rgba(160,196,224,.14)",
    background: "linear-gradient(180deg, #05080D, #03050A)",
    nameColor: "rgba(232,238,244,.8)",
    bestForColor: "rgba(232,238,244,.5)",
    featureColor: "rgba(232,238,244,.75)",
    borderTop: "1px solid rgba(160,196,224,.12)",
    features: [
      { text: "Up to 3 brands · 300 prompts per engine across the workspace" },
      { text: "ChatGPT · Gemini · Google AI Mode · AI Overviews · Perplexity" },
      { text: "Daily runs, citations every run" },
      { text: "60 Actions/month · 3 approaches each" },
      { text: "200 content pieces · 4 AEO scans" },
      { text: "5,000 credits/month" },
      { text: "Claude add-on $199/month", color: CYAN_SOFT },
    ],
    cta: "Start free",
    href: "/signup",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    bestFor: "Large brands and agency networks",
    priceM: null,
    priceA: null,
    noteM: "Configured to your workspace",
    featured: false,
    border: "1px solid rgba(160,196,224,.14)",
    background: "linear-gradient(180deg, #05080D, #03050A)",
    nameColor: "rgba(232,238,244,.8)",
    bestForColor: "rgba(232,238,244,.5)",
    featureColor: "rgba(232,238,244,.75)",
    borderTop: "1px solid rgba(160,196,224,.12)",
    features: [
      { text: "3+ brands · 200 to 500 prompts per engine" },
      { text: "All configured surfaces, including Claude" },
      { text: "Daily runs, citations every run" },
      { text: "Custom Actions capacity · 3 approaches each" },
      { text: "400 content pieces · 8 AEO scans" },
      { text: "12,000 credits/month" },
      { text: "Configured integrations · GSC and GA where included" },
    ],
    cta: "Talk to us",
    href: "/contact",
  },
]

/* ─── "What the units mean" cards ───────────────────────────────────────── */
const UNITS: { term: string; def: string }[] = [
  {
    term: "Prompt per engine",
    def: "One buyer question monitored separately on every included AI surface. 50 prompts across two engines is 100 answer runs per daily cycle.",
  },
  {
    term: "Action",
    def: "One prioritised workstream from a website, content, visibility or source gap, always with three proposed approaches.",
  },
  {
    term: "Content piece",
    def: "One review-ready asset: article, comparison page, buyer guide, FAQ set, structured-data block, website copy, outreach draft or correction brief.",
  },
  {
    term: "AEO scan & credit",
    def: "A scan audits your site's readiness for AI answers. Credits cover on-demand work beyond scheduled runs and monthly allowances.",
  },
]

/* ─── Compare plans table ───────────────────────────────────────────────── */
interface Cell {
  text: string
  color?: string
}
const COMPARE_HEADER: Cell[] = [
  { text: "Included", color: "rgba(232,238,244,.6)" },
  { text: "Starter", color: "rgba(232,238,244,.8)" },
  { text: "Pro", color: "#DEF6FC" },
  { text: "Scale", color: "rgba(232,238,244,.8)" },
  { text: "Enterprise", color: "rgba(232,238,244,.8)" },
]
const COMPARE_ROWS: { label: string; cells: Cell[] }[] = [
  { label: "Brands", cells: [{ text: "1" }, { text: "1" }, { text: "Up to 3" }, { text: "3 or more" }] },
  {
    label: "Tracked prompts per engine",
    cells: [{ text: "50" }, { text: "100" }, { text: "300 workspace-wide" }, { text: "200 to 500" }],
  },
  {
    label: "AI surfaces",
    cells: [
      { text: "ChatGPT, Gemini" },
      { text: "+ AI Mode, AI Overviews, Perplexity" },
      { text: "+ AI Mode, AI Overviews, Perplexity" },
      { text: "All configured" },
    ],
  },
  {
    label: "Claude",
    cells: [
      { text: "Not available", color: AMBER },
      { text: "+$79/month" },
      { text: "+$199/month" },
      { text: "Included", color: "rgba(143,217,196,.9)" },
    ],
  },
  { label: "Actions per month", cells: [{ text: "15" }, { text: "20" }, { text: "60" }, { text: "Custom" }] },
  { label: "Approaches per Action", cells: [{ text: "3" }, { text: "3" }, { text: "3" }, { text: "3" }] },
  { label: "Content pieces per month", cells: [{ text: "30" }, { text: "60" }, { text: "200" }, { text: "400" }] },
  { label: "AEO scans per month", cells: [{ text: "1" }, { text: "2" }, { text: "4" }, { text: "8" }] },
  { label: "Included credits", cells: [{ text: "400" }, { text: "1,000" }, { text: "5,000" }, { text: "12,000" }] },
  {
    label: "Integrations",
    cells: [
      { text: "Not included", color: "rgba(232,238,244,.4)" },
      { text: "Not included", color: "rgba(232,238,244,.4)" },
      { text: "Not included", color: "rgba(232,238,244,.4)" },
      { text: "Configured, incl. GSC and GA where included" },
    ],
  },
]

/* ─── On-demand credits ─────────────────────────────────────────────────── */
const CREDIT_ROWS: { name: string; sub: string; bar: string; credits: string }[] = [
  { name: "Extra Action", sub: "Includes three proposed approaches", bar: "100%", credits: "100" },
  { name: "Full refresh across all prompts and engines", sub: "With citations collected", bar: "100%", credits: "300" },
  { name: "Extra AEO scan", sub: "Technical, access and answer readiness", bar: "100%", credits: "300" },
  { name: "Extra proposal", sub: "One more approach on an existing Action", bar: "30%", credits: "30" },
  { name: "Extra content piece", sub: "Article, comparison page, FAQ set or brief", bar: "30%", credits: "30" },
]

const PACKS: { credits: string; price: string; best: boolean }[] = [
  { credits: "500", price: "$10", best: false },
  { credits: "1,500", price: "$25", best: false },
  { credits: "3,500", price: "$50", best: false },
  { credits: "20,000", price: "$250", best: true },
]

const FAQS: { q: string; a: string }[] = [
  {
    q: "How does annual billing work?",
    a: "Annual plans are billed upfront at the price of ten months, so two months are free.",
  },
  {
    q: "What happens when I run out of credits?",
    a: "Scheduled runs and your monthly allowances continue. Extra on-demand work draws from credits, and you can top up with a credit pack at any time.",
  },
  {
    q: "Is Claude available on every plan?",
    a: "Claude is not available on Starter. It is a $79/month add-on on Pro and $199/month on Scale, and is included in configured Enterprise workspaces.",
  },
  {
    q: "Does Velova publish changes for me?",
    a: "No. Every prepared Action and content piece stays reviewable and editable. You approve or reject before anything is implemented.",
  },
]

/* ─── Shared bits ───────────────────────────────────────────────────────── */
function SectionEyebrow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span
        style={{
          flex: "none",
          font: `500 10px/1 ${MONO}`,
          letterSpacing: ".18em",
          textTransform: "uppercase",
          color: "rgba(190,233,245,.85)",
        }}
      >
        {label}
      </span>
      <span
        aria-hidden="true"
        style={{ flex: 1, height: 1, background: "linear-gradient(90deg, rgba(107,213,236,.35), transparent)" }}
      />
    </div>
  )
}

const CTA_PRIMARY: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  height: 42,
  borderRadius: 10,
  border: "1px solid transparent",
  background: "linear-gradient(180deg, #8AE2F4, #4FC2DC)",
  font: `600 10px/1 ${MONO}`,
  letterSpacing: ".14em",
  textTransform: "uppercase",
  color: "#04161C",
  boxShadow: "0 14px 34px -18px rgba(107,213,236,.6)",
}
const CTA_GHOST: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  height: 42,
  borderRadius: 10,
  border: "1px solid rgba(160,196,224,.28)",
  font: `600 10px/1 ${MONO}`,
  letterSpacing: ".14em",
  textTransform: "uppercase",
  color: "#DCEAF2",
}

/* ─── Plan card ─────────────────────────────────────────────────────────── */
function PlanCard({ plan, billing }: { plan: Plan; billing: Billing }) {
  const isCustom = plan.priceM === null
  const price = isCustom ? "Custom" : billing === "annual" ? plan.priceA : plan.priceM
  const per = isCustom ? null : billing === "annual" ? "/year" : "/month"
  const note = isCustom ? plan.noteM : billing === "annual" ? "Ten months upfront, two months free" : plan.noteM

  return (
    <div
      data-p="plan"
      style={{
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        padding: 22,
        borderRadius: 16,
        border: plan.border,
        background: plan.background,
        boxShadow: plan.boxShadow,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, minHeight: 20 }}>
        <span
          style={{
            font: `500 10px/1 ${MONO}`,
            letterSpacing: ".16em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            color: plan.nameColor,
          }}
        >
          {plan.name}
        </span>
        {plan.featured && (
          <span
            style={{
              font: `500 8.5px/1 ${MONO}`,
              letterSpacing: ".12em",
              textTransform: "uppercase",
              color: "#6BD5EC",
              border: "1px solid rgba(107,213,236,.45)",
              padding: "4px 6px",
              borderRadius: 5,
            }}
          >
            Recommended
          </span>
        )}
      </div>

      <div style={{ fontSize: 12, lineHeight: 1.5, color: plan.bestForColor, marginTop: 8 }}>{plan.bestFor}</div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 18 }}>
        <span style={{ fontSize: 32, fontWeight: 600, letterSpacing: "-.03em", color: "#fff" }}>{price}</span>
        {per && <span style={{ fontSize: 12, color: "rgba(232,238,244,.5)" }}>{per}</span>}
      </div>

      <div style={{ font: `400 10.5px/1.5 ${MONO}`, color: "rgba(232,238,244,.6)", marginTop: 6 }}>{note}</div>

      {/* Every plan lists the SAME seven dimensions in the SAME order, so each
          one gets a fixed-height slot (.vx-plan-features > span in the
          stylesheet below). Row N therefore sits at an identical y in
          all four cards and reads straight across as a ruled comparison row —
          previously the wrapping strings drifted the last row 55px apart. The
          hairline between rows is what carries the eye horizontally. */}
      <div
        className="vx-plan-features"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          marginTop: 18,
          paddingTop: 4,
          borderTop: plan.borderTop,
          fontSize: 12.5,
          lineHeight: 1.45,
          color: plan.featureColor,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {plan.features.map((f, i) => (
          <span
            key={f.text}
            style={{
              display: "flex",
              alignItems: "center",
              paddingBlock: 8,
              borderTop: i === 0 ? undefined : plan.borderTop,
              textWrap: "pretty",
              ...(f.color ? { color: f.color } : null),
            }}
          >
            {f.text}
          </span>
        ))}
      </div>

      <Link
        href={plan.href}
        data-c={plan.featured ? "primary" : "ghost"}
        style={{ ...(plan.featured ? CTA_PRIMARY : CTA_GHOST), marginTop: 20 }}
      >
        {plan.cta}
      </Link>
    </div>
  )
}

/* ─── Billing-questions accordion item ──────────────────────────────────── */
function FaqItem({ q, a, defaultOpen }: { q: string; a: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(Boolean(defaultOpen))
  return (
    <div style={{ borderBottom: "1px solid rgba(160,196,224,.1)" }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "14px 0",
          width: "100%",
          background: "transparent",
          border: 0,
          textAlign: "left",
          fontFamily: "var(--vx-sans)",
          fontSize: 13.5,
          color: open ? "#F6FAFD" : "#E8EEF4",
        }}
      >
        <span>{q}</span>
        <span
          aria-hidden="true"
          style={{
            position: "relative",
            flex: "none",
            width: 10,
            height: 1,
            background: "rgba(190,233,245,.85)",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 4.5,
              top: -4.5,
              width: 1,
              height: 10,
              background: "rgba(190,233,245,.85)",
              opacity: open ? 0 : 1,
              transition: "opacity .25s ease",
            }}
          />
        </span>
      </button>
      {/* grid-template-rows 0fr→1fr instead of max-height 0→200px. The old
          version animated a layout property (thrash) AND capped the panel at a
          magic 200px: fine on desktop where answers are 54px, but they grow to
          74px at 390px, and a longer answer, a 320px phone or a larger
          accessibility font size would have silently truncated one. This
          animates to the content's own height, whatever it is. */}
      <div
        style={{
          display: "grid",
          gridTemplateRows: open ? "1fr" : "0fr",
          opacity: open ? 1 : 0,
          transition: "grid-template-rows .35s cubic-bezier(.2,.7,.2,1), opacity .3s ease",
        }}
      >
        <div style={{ overflow: "hidden", minHeight: 0 }}>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "rgba(232,238,244,.68)", padding: "0 0 14px" }}>{a}</div>
        </div>
      </div>
    </div>
  )
}

/* ─── Billing toggle ────────────────────────────────────────────────────── */
function BillingToggle({ billing, onChange }: { billing: Billing; onChange: (b: Billing) => void }) {
  const active: React.CSSProperties = {
    background: "rgba(107,213,236,.16)",
    border: "1px solid rgba(107,213,236,.45)",
    color: "#DEF6FC",
  }
  const inactive: React.CSSProperties = {
    background: "transparent",
    border: "1px solid transparent",
    color: "rgba(232,238,244,.6)",
  }
  const base: React.CSSProperties = {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    height: 32,
    padding: "0 14px",
    borderRadius: 9,
    font: `600 9.5px/1 ${MONO}`,
    letterSpacing: ".14em",
    textTransform: "uppercase",
  }
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 0,
        padding: 4,
        borderRadius: 12,
        border: "1px solid rgba(160,196,224,.18)",
        background: "rgba(8,17,25,.9)",
      }}
    >
      <button
        type="button"
        aria-pressed={billing === "monthly"}
        onClick={() => onChange("monthly")}
        style={{ ...base, ...(billing === "monthly" ? active : inactive) }}
      >
        Monthly
      </button>
      <button
        type="button"
        aria-pressed={billing === "annual"}
        onClick={() => onChange("annual")}
        style={{ ...base, ...(billing === "annual" ? active : inactive) }}
      >
        Annual
      </button>
    </div>
  )
}

/* ─── Page body ─────────────────────────────────────────────────────────── */
export default function PricingClient() {
  const [billing, setBilling] = useState<Billing>("monthly")

  return (
    <div className="vx-pricing" style={{ position: "relative", paddingBottom: 80 }}>
      {/* top glow */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "min(1200px, 100%)",
          height: 620,
          pointerEvents: "none",
          background:
            "radial-gradient(46% 46% at 50% 6%, rgba(107,213,236,.1), transparent 70%), radial-gradient(40% 40% at 84% 30%, rgba(56,196,184,.07), transparent 72%)",
          filter: "blur(44px)",
        }}
      />

      {/* HERO */}
      <section style={{ position: "relative", paddingTop: 64 }}>
        <div
          className="vx-hero-row"
          style={{ ...CONTAINER, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 60, flexWrap: "wrap" }}
        >
          <div className="vx-in" style={{ flex: "1 1 520px", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 2,
                  transform: "rotate(45deg)",
                  background: "#6BD5EC",
                  boxShadow: "0 0 14px rgba(107,213,236,.6)",
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  font: `500 11px/1 ${MONO}`,
                  letterSpacing: ".26em",
                  textTransform: "uppercase",
                  color: "rgba(190,233,245,.85)",
                }}
              >
                Pricing
              </span>
            </div>
            <h1
              style={{
                margin: "18px 0 0",
                fontSize: "clamp(31px, 4.5vw, 46px)",
                lineHeight: 1.05,
                fontWeight: 500,
                letterSpacing: "-.03em",
                color: "#F4F8FB",
              }}
            >
              Track the answers that decide your category.
            </h1>
            <div
              style={{
                fontFamily: SERIF,
                fontStyle: "italic",
                fontSize: "clamp(21px, 2.4vw, 27px)",
                lineHeight: 1.2,
                color: "rgba(178,224,240,.9)",
                marginTop: 8,
              }}
            >
              Every plan includes Actions.
            </div>
          </div>

          <div className="vx-hero-aside" style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
            <BillingToggle billing={billing} onChange={setBilling} />
            <span
              style={{
                fontSize: 12.5,
                lineHeight: 1.5,
                color: "rgba(143,217,196,.85)",
                textAlign: "right",
              }}
            >
              Annual is billed upfront at ten months, so two months are free.
            </span>
          </div>
        </div>
      </section>

      {/* PLAN CARDS */}
      <section style={{ position: "relative", paddingTop: 36 }}>
        <div className="vx-plans" style={CONTAINER}>
          {PLANS_DATA.map((plan) => (
            <PlanCard key={plan.id} plan={plan} billing={billing} />
          ))}
        </div>
      </section>

      {/* WHAT THE UNITS MEAN */}
      <section style={{ position: "relative", paddingTop: 64 }}>
        <div style={CONTAINER}>
          <SectionEyebrow label="What the units mean" />
          <div className="vx-units" style={{ marginTop: 18 }}>
            {UNITS.map((u) => (
              <div
                key={u.term}
                style={{
                  border: "1px solid rgba(160,196,224,.14)",
                  borderRadius: 14,
                  background: "#05080D",
                  padding: 18,
                }}
              >
                <div
                  style={{
                    font: `500 9.5px/1 ${MONO}`,
                    letterSpacing: ".16em",
                    textTransform: "uppercase",
                    color: "#DEF6FC",
                  }}
                >
                  {u.term}
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "rgba(232,238,244,.7)", marginTop: 10 }}>
                  {u.def}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARE PLANS */}
      <section style={{ position: "relative", paddingTop: 64 }}>
        <div style={CONTAINER}>
          <SectionEyebrow label="Compare plans" />
          <div className="vx-compare-scroll" style={{ marginTop: 18, overflowX: "auto" }}>
            <div
              style={{
                minWidth: 720,
                border: "1px solid rgba(160,196,224,.14)",
                borderRadius: 16,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.6fr 1fr 1fr 1fr 1fr",
                  background: "rgba(107,213,236,.06)",
                  borderBottom: "1px solid rgba(160,196,224,.14)",
                }}
              >
                {COMPARE_HEADER.map((h) => (
                  <span
                    key={h.text}
                    style={{
                      padding: 16,
                      font: `600 12px/1 ${MONO}`,
                      letterSpacing: ".12em",
                      textTransform: "uppercase",
                      color: h.color,
                    }}
                  >
                    {h.text}
                  </span>
                ))}
              </div>
              {COMPARE_ROWS.map((row, i) => (
                <div
                  key={row.label}
                  data-p="row"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.6fr 1fr 1fr 1fr 1fr",
                    borderBottom: i === COMPARE_ROWS.length - 1 ? undefined : "1px solid rgba(160,196,224,.08)",
                    fontSize: 12.5,
                    color: "rgba(232,238,244,.8)",
                  }}
                >
                  <span style={{ padding: "12px 16px", fontWeight: 600, color: "rgba(240,245,249,.92)" }}>
                    {row.label}
                  </span>
                  {row.cells.map((c, ci) => (
                    <span key={ci} style={{ padding: "12px 16px", color: c.color }}>
                      {c.text}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "rgba(232,238,244,.55)", marginTop: 14 }}>
            All prepared work stays reviewable and editable. You review, edit, approve or reject before anything is
            implemented.
          </div>
        </div>
      </section>

      {/* ON-DEMAND CREDITS + BILLING QUESTIONS */}
      <section style={{ position: "relative", paddingTop: 64 }}>
        <div className="vx-lower" style={CONTAINER}>
          {/* credits */}
          <div
            style={{
              border: "1px solid rgba(160,196,224,.14)",
              borderRadius: 16,
              background: "linear-gradient(180deg, #06090F, #030509)",
              padding: 22,
            }}
          >
            <div>
              <div
                style={{
                  font: `500 10px/1 ${MONO}`,
                  letterSpacing: ".18em",
                  textTransform: "uppercase",
                  color: "rgba(190,233,245,.85)",
                }}
              >
                On-demand credits
              </div>
              <div style={{ fontSize: 19, fontWeight: 500, letterSpacing: "-.02em", color: "#F4F8FB", marginTop: 10 }}>
                Go beyond your monthly allowances.
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.55,
                  color: "rgba(232,238,244,.55)",
                  maxWidth: 420,
                  marginTop: 6,
                  textWrap: "pretty",
                }}
              >
                Credits only apply to extra work you request. Scheduled runs stay included.
              </div>
            </div>

            <div style={{ marginTop: 18, paddingTop: 4, borderTop: "1px solid rgba(160,196,224,.12)" }}>
              {CREDIT_ROWS.map((r, i) => (
                <div
                  key={r.name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "13px 0",
                    borderBottom:
                      i === CREDIT_ROWS.length - 1 ? undefined : "1px solid rgba(160,196,224,.09)",
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13, color: "#F2F7FA" }}>{r.name}</span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 11.5,
                        lineHeight: 1.45,
                        color: "rgba(232,238,244,.5)",
                        marginTop: 3,
                      }}
                    >
                      {r.sub}
                    </span>
                  </span>
                  <span style={{ flex: "0 0 96px" }}>
                    <span
                      aria-hidden="true"
                      style={{
                        display: "block",
                        height: 3,
                        borderRadius: 2,
                        background: "linear-gradient(90deg, rgba(107,213,236,.85), rgba(107,213,236,.15))",
                        width: r.bar,
                      }}
                    />
                  </span>
                  <span
                    style={{
                      flex: "0 0 62px",
                      textAlign: "right",
                      font: `600 15px/1 ${MONO}`,
                      color: "#DEF6FC",
                    }}
                  >
                    {r.credits}
                  </span>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18 }}>
              <span
                style={{
                  flex: "none",
                  font: `500 9.5px/1 ${MONO}`,
                  letterSpacing: ".16em",
                  textTransform: "uppercase",
                  color: "rgba(232,238,244,.6)",
                }}
              >
                Credit packs
              </span>
              <span
                aria-hidden="true"
                style={{ flex: 1, height: 1, background: "linear-gradient(90deg, rgba(107,213,236,.3), transparent)" }}
              />
            </div>

            <div className="vx-packs" style={{ marginTop: 12 }}>
              {PACKS.map((p) => (
                <div
                  key={p.credits}
                  className="vx-pack"
                  style={{
                    boxSizing: "border-box",
                    padding: "14px 13px",
                    borderRadius: 12,
                    border: p.best ? "1px solid rgba(107,213,236,.45)" : "1px solid rgba(160,196,224,.14)",
                    background: p.best ? "linear-gradient(180deg, rgba(107,213,236,.1), #04070C)" : "#04070C",
                  }}
                >
                  <div style={{ font: `600 17px/1 ${MONO}`, color: "#F6FAFD" }}>{p.credits}</div>
                  <div
                    style={{
                      font: `400 9.5px/1 ${MONO}`,
                      letterSpacing: ".12em",
                      textTransform: "uppercase",
                      color: "rgba(232,238,244,.5)",
                      marginTop: 6,
                    }}
                  >
                    credits
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 8,
                      marginTop: 12,
                      paddingTop: 11,
                      borderTop: "1px solid rgba(160,196,224,.12)",
                    }}
                  >
                    <span style={{ fontSize: 15, fontWeight: 600, color: p.best ? "#6BD5EC" : "#E8EEF4" }}>
                      {p.price}
                    </span>
                    {p.best && (
                      <span
                        style={{
                          font: `500 8px/1 ${MONO}`,
                          letterSpacing: ".12em",
                          textTransform: "uppercase",
                          color: "#6BD5EC",
                        }}
                      >
                        Best value
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* billing questions */}
          <div
            style={{
              border: "1px solid rgba(160,196,224,.14)",
              borderRadius: 16,
              background: "#05080D",
              padding: 22,
            }}
          >
            <div
              style={{
                font: `500 10px/1 ${MONO}`,
                letterSpacing: ".18em",
                textTransform: "uppercase",
                color: "rgba(190,233,245,.85)",
              }}
            >
              Billing questions
            </div>
            <div style={{ marginTop: 12 }}>
              {FAQS.map((f, i) => (
                <FaqItem key={f.q} q={f.q} a={f.a} defaultOpen={i === 0} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{ position: "relative", marginTop: 64 }}>
        <div style={CONTAINER}>
          <div
            className="vx-final-cta"
            style={{
              border: "1px solid rgba(107,213,236,.22)",
              borderRadius: 18,
              background: "linear-gradient(120deg, rgba(107,213,236,.1), rgba(4,7,12,.9) 60%)",
              padding: "28px 30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 30,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontSize: 26, fontWeight: 500, letterSpacing: "-.025em", color: "#F4F8FB" }}>
                See how AI presents your brand.
              </div>
              <div
                style={{
                  fontFamily: SERIF,
                  fontStyle: "italic",
                  fontSize: 19,
                  color: "rgba(178,224,240,.9)",
                  marginTop: 5,
                }}
              >
                Start with one buyer question, free.
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <Link href="/signup" data-c="primary" style={{ ...CTA_PRIMARY, height: 42, padding: "0 20px", borderRadius: 11 }}>
                Run a free test
              </Link>
              <Link href="/contact" data-c="ghost" style={{ ...CTA_GHOST, height: 42, padding: "0 20px", borderRadius: 11 }}>
                Talk to us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* scoped responsive + hover behaviour */}
      <style dangerouslySetInnerHTML={{ __html: `
        .vx-pricing [data-p="plan"] {
          transition: transform .3s cubic-bezier(.2,.7,.2,1), border-color .3s ease, box-shadow .3s ease, background .3s ease;
        }
        .vx-pricing [data-p="plan"]:hover { transform: translateY(-4px); }
        .vx-pricing [data-p="row"]:hover { background: rgba(120,170,210,.04); }

        .vx-pricing .vx-plans {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px;
          align-items: stretch;
        }
        /* Fixed slot per feature so row N lines up across side-by-side cards.
           Lives in CSS, not inline, so the single-column phone layout can drop
           it — see the 640px block below. */
        .vx-pricing .vx-plan-features > span { min-height: 53px; }
        .vx-pricing .vx-units {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px;
        }
        .vx-pricing .vx-lower {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 20px;
          align-items: start;
        }
        .vx-pricing .vx-packs { display: flex; gap: 10px; }
        .vx-pricing .vx-pack { flex: 1 1 0; min-width: 0; }

        @media (max-width: 1080px) {
          .vx-pricing .vx-plans { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .vx-pricing .vx-units { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .vx-pricing .vx-lower { grid-template-columns: 1fr; }
        }
        @media (max-width: 640px) {
          .vx-pricing .vx-plans { grid-template-columns: 1fr; }
          /* Cards are stacked now, so two of them are never on screen together
             and the fixed slot buys no alignment — it only padded ~35px of dead
             space onto every one-line row (≈980px of extra scroll across the
             four cards). Let rows size to their text on phones. */
          .vx-pricing .vx-plan-features > span { min-height: 0; padding-block: 10px; }
          .vx-pricing .vx-units { grid-template-columns: 1fr; }
          .vx-pricing .vx-hero-row { align-items: flex-start; }
          /* stacked hero: the toggle + note follow the headline's left edge
             instead of hanging off the right rail */
          .vx-pricing .vx-hero-aside { align-items: flex-start; }
          .vx-pricing .vx-hero-aside > span { text-align: left; }
          .vx-pricing .vx-packs { flex-wrap: wrap; }
          .vx-pricing .vx-pack { flex: 1 1 44%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .vx-pricing [data-p="plan"] { transition: none !important; }
        }
      ` }} />
    </div>
  )
}
